import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import {
  Assessment as SummarizeIcon,
  Close,
  Group,
  Schedule,
  Message,
  Person,
  Refresh,
  FileCopy,
  GetApp,
  ExpandMore,
  Today as TodayIcon,
  Share as ShareIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Topic as TopicIcon,
  SentimentSatisfied as SentimentNeutralIcon,
  Help as QuestionIcon,
  PlayArrow as PlayArrowIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
  },
  titleIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  summaryCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  summaryContent: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
  },
  statsGrid: {
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    textAlign: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  participantsList: {
    maxHeight: 200,
    overflow: "auto",
  },
  keyPointItem: {
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  filterSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  configSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const GroupSummaryModal = ({ open, onClose, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useSettings();
  const [settings, setSettings] = useState({});

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("24h");
  const [summaryType, setSummaryType] = useState("full");
  const [step, setStep] = useState(1); // 1: Config, 2: Results
  const [configValidation, setConfigValidation] = useState(null);
  const [messageSent, setMessageSent] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendToGroup, setSendToGroup] = useState(false);

  // Estados para configurações locais
  const [localConfig, setLocalConfig] = useState({
    summaryLanguage: 'pt-BR',
    summaryLength: 'medium',
    includeParticipantStats: true
  });

  // Carregar configurações
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = await getAllSettings();
        const settingsMap = {};
        settingsData.forEach(setting => {
          settingsMap[setting.key] = setting.value;
        });
        setSettings(settingsMap);
      } catch (error) {
        console.warn('Erro ao carregar configurações:', error);
        setSettings({});
      }
    };
    loadSettings();
  }, [getAllSettings]);

  // Helper function to get setting value
  const getSettingValue = useCallback((key) => {
    return settings[key] || null;
  }, [settings]);

  const periodOptions = [
    { value: "1h", label: "Última hora" },
    { value: "6h", label: "Últimas 6 horas" },
    { value: "24h", label: "Últimas 24 horas" },
    { value: "3d", label: "Últimos 3 dias" },
    { value: "7d", label: "Última semana" },
    { value: "30d", label: "Último mês" },
  ];

  const summaryTypeOptions = [
    { value: "full", label: "Resumo Completo" },
    { value: "key_points", label: "Pontos Principais" },
    { value: "participants", label: "Participantes Ativos" },
    { value: "topics", label: "Tópicos Discutidos" },
  ];

  // Verificações
  const hasOpenAIKey = !!(getSettingValue("openAiKey"));
  const isValidGroupTicket = ticket && ticket.isGroup && ticket.contact;

  // Carregar configurações ao abrir
  useEffect(() => {
    if (open) {
      loadLocalConfig();
      if (hasOpenAIKey) {
        validateConfiguration();
      }
    }
  }, [open, hasOpenAIKey]);

  const loadLocalConfig = () => {
    const savedConfig = localStorage.getItem('groupSummaryConfig');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setLocalConfig(prev => ({ ...prev, ...parsedConfig }));

        if (parsedConfig.sendToGroup !== undefined) {
          setSendToGroup(parsedConfig.sendToGroup);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações locais:', error);
      }
    }
  };

  const saveLocalConfig = () => {
    const configToSave = { ...localConfig, sendToGroup };
    localStorage.setItem('groupSummaryConfig', JSON.stringify(configToSave));
  };

  const validateConfiguration = async () => {
    try {
      const response = await api.get('/group-summary/validate-config');
      setConfigValidation(response.data);

      if (!response.data.isValid) {
        toastUtils.error('Configurações de IA inválidas. Verifique as configurações.', 'Erro');
      } else if (response.data.warnings && response.data.warnings.length > 0) {
        response.data.warnings.forEach(warning => {
          toastUtils.warning(warning, 'Aviso');
        });
      }
    } catch (error) {
      console.error('Erro ao validar configurações:', error);
      setConfigValidation({ isValid: false, error: error.message });
    }
  };

  const handleClose = () => {
    setStep(1);
    setSummary(null);
    setConfigValidation(null);
    setMessageSent(false);
    setSendError(null);
    onClose();
  };

  const handleConfigChange = (key, value) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerateSummary = async () => {
    if (!hasOpenAIKey) {
      toastUtils.error('Configurações de IA não encontradas.', 'Erro');
      return;
    }

    if (!isValidGroupTicket) {
      toastUtils.error('Ticket inválido para geração de resumo.', 'Erro');
      return;
    }

    if (!configValidation || !configValidation.isValid) {
      toastUtils.error('Configurações de IA inválidas.', 'Erro');
      return;
    }

    setLoading(true);
    try {
      saveLocalConfig();

      const requestConfig = {
        ticketId: ticket.id,
        period,
        summaryType,
        sendToGroup
      };

      const response = await api.post('/group-summary/generate', requestConfig);

      setSummary(response.data.summary);
      setMessageSent(response.data.messageSent || false);
      setSendError(response.data.sendError || null);
      setStep(2);

      if (sendToGroup) {
        if (response.data.messageSent) {
          toastUtils.success('Resumo gerado e enviado para o grupo com sucesso!', 'Sucesso');
        } else if (response.data.sendError) {
          toastUtils.warning(`Resumo gerado com sucesso, mas houve erro ao enviar: ${response.data.sendError}`, 'Aviso');
        } else {
          toastUtils.success('Resumo gerado com sucesso!', 'Sucesso');
        }
      } else {
        toastUtils.success('Resumo gerado com sucesso!', 'Sucesso');
      }
    } catch (error) {
      console.error('Erro ao gerar resumo:', error);

      if (error.response && error.response.status === 401) {
        toastUtils.error('Chave da OpenAI inválida.', 'Erro');
      } else if (error.response && error.response.status === 400) {
        const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Dados inválidos para gerar resumo';
        toastUtils.error(errorMessage, 'Erro');
      } else if (error.response && error.response.status === 503) {
        toastUtils.error('Serviço de IA temporariamente indisponível.', 'Erro');
      } else {
        const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Erro ao gerar resumo';
        toastUtils.error(errorMessage, 'Erro');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyFinalMessage = () => {
    if (!summary || !summary.finalMessage) return;

    navigator.clipboard.writeText(summary.finalMessage).then(() => {
      toastUtils.success('Mensagem pronta copiada para área de transferência!', 'Copiado');
    }).catch(() => {
      toastUtils.error('Erro ao copiar mensagem', 'Erro');
    });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('pt-BR');
  };

  const renderConfigStep = () => {
    if (!hasOpenAIKey) {
      return (
        <Box p={2}>
          <Typography variant="body1" color="error">
            <strong>Configurações de IA não encontradas</strong><br />
            Para usar a funcionalidade de resumo de grupos, é necessário que as configurações de IA estejam definidas.
          </Typography>
        </Box>
      );
    }

    if (!isValidGroupTicket) {
      return (
        <Box p={2}>
          <Typography variant="body1" color="error">
            <strong>Ticket inválido</strong><br />
            Este ticket não é válido para geração de resumo de grupo.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Grid container spacing={3}>
          {/* Configurações de Período */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Período do Resumo
            </Typography>

            <FormControl fullWidth style={{ marginBottom: 16 }}>
              <InputLabel>{i18n.t("aiSuggestions.groupSummary.periodLabel")}</InputLabel>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {periodOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 16 }}>
              <InputLabel>Tipo de Resumo</InputLabel>
              <Select
                value={summaryType}
                onChange={(e) => setSummaryType(e.target.value)}
              >
                {summaryTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Configurações de Envio */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Envio Automático
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={sendToGroup}
                  onChange={(e) => setSendToGroup(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Enviar resumo automaticamente para o grupo
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    A mensagem de resumo será enviada diretamente no grupo do WhatsApp
                  </Typography>
                </Box>
              }
            />

            {sendToGroup && (
              <Box p={1} mt={1} style={{ backgroundColor: '#e3f2fd', borderRadius: 4 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <WhatsAppIcon color="primary" />
                  <Typography variant="body2">
                    O resumo será enviado para: <strong>{(ticket && ticket.contact && ticket.contact.name) || 'Grupo'}</strong>
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Status da Configuração */}
        {configValidation && (
          <Box mt={3}>
            {configValidation.isValid ? (
              <Box p={2} style={{ backgroundColor: '#e8f5e8', borderRadius: 4 }}>
                <Typography variant="body2" color="primary">
                  <strong>✓ Configurações válidas</strong><br />
                  Modelo: {configValidation.model}<br />
                  Chave API: Configurada
                </Typography>
              </Box>
            ) : (
              <Box p={2} style={{ backgroundColor: '#ffebee', borderRadius: 4 }}>
                <Typography variant="body2" color="error">
                  <strong>✗ Configurações inválidas</strong><br />
                  {configValidation.error}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderResultsStep = () => {
    return (
      <Box>
        {/* Status do envio se foi solicitado */}
        {sendToGroup && (
          <Box mb={3}>
            {messageSent ? (
              <Box p={2} style={{ backgroundColor: '#e8f5e8', borderRadius: 4 }}>
                <Typography variant="body1" color="primary">
                  <strong>Resumo enviado com sucesso!</strong><br />
                  A mensagem foi enviada para o grupo <strong>{summary && summary.groupName}</strong>
                </Typography>
              </Box>
            ) : sendError ? (
              <Box p={2} style={{ backgroundColor: '#fff3e0', borderRadius: 4 }}>
                <Typography variant="body1" color="secondary">
                  <strong>Resumo gerado, mas houve erro no envio</strong><br />
                  {sendError}
                </Typography>
              </Box>
            ) : (
              <Box p={2} style={{ backgroundColor: theme.palette.type === 'dark' ? '#0d47a1' : '#e3f2fd', borderRadius: 4 }}>
                <Typography variant="body1">
                  Envio automático estava habilitado, mas não foi possível confirmar o status.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Estatísticas Principais */}
        {summary && (
          <Box p={2} mb={2} style={{ backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#f5f5f5', borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom>
              📊 Estatísticas do Período
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip label={`${summary.stats?.totalInteractions || 0} interações`} color="primary" />
              <Chip label={`${summary.stats?.totalReactions || 0} reações`} color="secondary" />
              <Chip label={`${summary.stats?.totalParticipants || 0} participantes`} />
              <Chip label={`${summary.stats?.averageInteractionsPerPerson || 0} média/pessoa`} />
            </Box>
          </Box>
        )}

        {/* Mensagem Final Pronta */}
        {summary?.finalMessage && (
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              📝 Mensagem Pronta para Envio
            </Typography>
            <Card style={{ backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#f5f5f5', maxHeight: 200, overflow: 'auto' }}>
              <CardContent>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {summary.finalMessage}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Detalhes Expandidos */}
        {summary && (
          <Box>
            {/* Top Participantes */}
            {summary.topParticipants?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">🏆 Top Participantes</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List style={{ width: '100%' }}>
                    {summary.topParticipants.map((participant, index) => (
                      <ListItem key={index}>
                        <ListItemAvatar>
                          <Chip
                            label={`${index + 1}º`}
                            size="small"
                            color={index === 0 ? "primary" : "default"}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={participant.name}
                          secondary={`${participant.totalInteractions} interações • ${participant.messageCount} mensagens • ${participant.reactionCount} reações`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Principais Tópicos */}
            {summary.aiSummary?.mainTopics?.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">🔝 Principais Tópicos</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List style={{ width: '100%' }}>
                    {summary.aiSummary.mainTopics.map((topic, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={topic} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

        {/* Informações de Processamento */}
        {summary && (
          <Box mt={3}>
            <Box p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: 4 }}>
              <Typography variant="body2">
                <strong>ℹ️ Informações do Processamento</strong><br />
                <strong>Modelo:</strong> {getSettingValue("openaiModel") || 'gpt-4o'}<br />
                <strong>Período:</strong> {summary.period?.date} • {summary.period?.startTime} - {summary.period?.endTime}<br />
                <strong>Tempo:</strong> {summary.metadata?.processingTime || 'N/A'}ms<br />
                <strong>Confiança:</strong> {Math.round((summary.metadata?.confidence || 0) * 100)}%
                {sendToGroup && (
                  <>
                    <br /><strong>Envio:</strong> {messageSent ? '✅ Enviado' : '❌ Falhou'}
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Definir ações do modal
  const getModalActions = () => {
    if (step === 1) {
      const actions = [
        {
          label: 'Cancelar',
          onClick: handleClose,
          variant: 'outlined'
        }
      ];

      if (hasOpenAIKey && configValidation?.isValid && isValidGroupTicket) {
        actions.push({
          label: loading ? 'Gerando Resumo...' : 'Gerar Resumo',
          onClick: handleGenerateSummary,
          variant: 'contained',
          disabled: loading,
          startIcon: loading ? <CircularProgress size={16} /> : <TodayIcon />
        });
      }

      return actions;
    } else {
      const actions = [
        {
          label: 'Voltar',
          onClick: () => setStep(1),
          variant: 'outlined'
        }
      ];

      // Ação para copiar mensagem pronta (principal)
      if (summary?.finalMessage) {
        actions.push({
          label: messageSent ? 'Copiar (Já Enviado)' : 'Copiar Mensagem',
          onClick: handleCopyFinalMessage,
          variant: 'contained',
          startIcon: messageSent ? <CheckIcon /> : <ShareIcon />,
          color: messageSent ? 'primary' : 'primary'
        });
      }

      actions.push({
        label: 'Fechar',
        onClick: handleClose,
        variant: 'outlined'
      });

      return actions;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <SummarizeIcon className={classes.titleIcon} />
          <Typography variant="h6">
            {i18n.t("aiSuggestions.groupSummaryTitle")}
          </Typography>
          {ticket && ticket.contact && ticket.contact.name && (
            <Chip
              icon={<Group />}
              label={ticket.contact.name}
              variant="outlined"
              size="small"
              style={{ marginLeft: 16 }}
            />
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          className={classes.closeButton}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="body2" style={{ marginTop: 16 }}>
              {i18n.t("aiSuggestions.groupSummary.analyzing")}
            </Typography>
          </Box>
        )}

        {!loading && (step === 1 ? renderConfigStep() : renderResultsStep())}
      </DialogContent>

      <DialogActions>
        {getModalActions().map((action, index) => (
          <Button
            key={index}
            variant={action.variant || 'outlined'}
            color={action.color || 'primary'}
            onClick={action.onClick}
            disabled={action.disabled}
            startIcon={action.startIcon}
          >
            {action.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default GroupSummaryModal;